import {
  CARROT_ROTATION_SPEED,
  Core,
  CoreInitParameters,
  RABBIT_RESPAWN_SCALE_DURATION,
  RABBIT_RESPAWN_SCALE_EASE,
  RABBIT_STATE,
} from '@/modules/advergaming/core'
import {
  AnimationClip,
  AnimationMixer,
  Group,
  LoopOnce,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  ShaderMaterial,
  Vector3,
} from 'three'
import gsap from 'gsap'
import { WritableAtom } from 'nanostores'
import { lerpDifficultyProperty, Params } from '@/modules/advergaming/utils'

import laneFragmentShader from '@/components/advergaming/lane.fragment.glsl?raw'

const PARAMS: Params = {
  cellSize: 2.5,
  section: {
    length: 10, // In cells
    count: 4,
    decoyCarrotChance: 0.05,
  },
  lane: {
    width: 2.5,
    length: 50,
    switchAnimationDuration: 0.0375,
  },
  player: {
    collisionRadius: 0.5,
  },
  difficulty: {
    lowDifficulty: {
      minCombo: 10,
      speed: 0.1,
      trapChance: 0.2,
      laneSwitchChance: 0.3,
      minDistanceBetweenTraps: 6,
      maxDistanceBetweenTraps: 10,
      removeCarrotChance: 0,
    },
    highDifficulty: {
      minCombo: 200,
      speed: 0.3,
      trapChance: 0.5,
      laneSwitchChance: 0.75,
      minDistanceBetweenTraps: 1,
      maxDistanceBetweenTraps: 2,
      removeCarrotChance: 0.5,
    },
  },
  respaw: {
    delay: 2,
  },
}

const KEY = {
  LEFT: 'ArrowLeft',
  RIGHT: 'ArrowRight',
} as const

const CELL = {
  EMPTY: 0,
  TRAP: 1,
  CARROT: 2,
} as const

const VECTOR_ZERO = Object.freeze(new Vector3(0, 0, 0))

type CellBlueprint = (typeof CELL)[keyof typeof CELL]
type SectionRowBlueprint = CellBlueprint[]
type SectionBlueprint = SectionRowBlueprint[]

class Item extends Object3D {
  collisionDetectionEnabled = true
  mesh: Object3D
  _type: typeof CELL.CARROT | typeof CELL.TRAP
  holder = new Group()
  mixer?: AnimationMixer

  constructor(type: Item['_type'], mesh: Object3D) {
    super()

    this.add(this.holder)

    this.mesh = mesh
    this.holder.add(this.mesh)

    this._type = type

    if (type === CELL.CARROT) {
      this.rotation.y = MathUtils.randFloat(0, Math.PI * 2)
    }

    if (type === CELL.TRAP) {
      this.mixer = new AnimationMixer(this.mesh)
    }
  }

  playAnimation(clips: AnimationClip[]) {
    clips.forEach(clip => {
      if (!this.mixer) {
        return
      }

      const action = this.mixer.clipAction(clip)
      action.clampWhenFinished = true
      action.setLoop(LoopOnce, 1).play()
    })
  }
}

class LevelSection extends Object3D {
  width: number
  length: number
  items: Item[] = []
  collisionDetectionEnabled: boolean = false

  constructor({
    blueprint,
    position,
    assets,
    prefab,
  }: {
    blueprint: SectionBlueprint
    position: number
    assets: {
      carrot: Object3D
      trap: Object3D
    }
    prefab: Mesh
  }) {
    super()

    this.width = blueprint[0].length * PARAMS.cellSize
    this.length = blueprint.length * PARAMS.cellSize

    this.initLanes(prefab)

    blueprint.forEach((row, rowIndex) => {
      row.forEach((cell, columnIndex) => {
        // if (cell === CELL.EMPTY) {
        //   return
        // }

        const x =
          columnIndex * PARAMS.cellSize - this.width / 2 + PARAMS.cellSize / 2

        const z =
          this.length -
          (rowIndex + 1) * PARAMS.cellSize -
          this.length / 2 +
          PARAMS.cellSize / 2

        if (cell === CELL.CARROT) {
          const item = new Item(CELL.CARROT, assets.carrot.clone())
          item.position.set(x, 0, z)
          this.add(item)
          this.items.push(item)
        } else if (cell === CELL.TRAP) {
          const item = new Item(CELL.TRAP, assets.trap.clone())
          item.position.set(x, 0, z)
          this.add(item)
          this.items.push(item)
        }
      })
    })

    this.position.z = position
  }

  initLanes(prefab: Mesh) {
    const LANES_COUNT = 3

    for (let i = 0; i < LANES_COUNT + 1; i++) {
      const clone = prefab.clone()

      clone.scale.y = this.length

      clone.position.x =
        i * PARAMS.lane.width - (LANES_COUNT * PARAMS.lane.width) / 2

      // Prevent z-fighting with ground mesh
      clone.position.y = 0.001

      this.add(clone)
    }
  }

  dispose() {
    this.traverse(object => {
      if (object instanceof Mesh) {
        object.geometry.dispose()
        object.material.dispose()
      }
    })
  }
}

class SectionGenerator {
  readonly ROW_COUNT = PARAMS.section.length
  readonly COL_COUNT = 3

  private safePathX = 1

  public laneSwitchChance = PARAMS.difficulty.lowDifficulty.laneSwitchChance
  private rowCountSinceLastLaneSwitch = -1
  public minRowCountUntilNewLaneSwitch = 1

  private rowCountSinceLastTrap = 0
  public minRowCountUntilNewTrap =
    PARAMS.difficulty.lowDifficulty.minDistanceBetweenTraps

  $combo: WritableAtom<number>

  constructor($combo: WritableAtom<number>) {
    this.$combo = $combo
  }

  generateSection(): SectionBlueprint {
    const blueprint: SectionBlueprint = []

    for (let y = 0; y < this.ROW_COUNT; y++) {
      const row = this.createBlueprintRow()
      blueprint.push(row)
    }

    return blueprint
  }

  createBlueprintRow() {
    const row: CellBlueprint[] = [CELL.EMPTY, CELL.EMPTY, CELL.EMPTY]
    row[this.safePathX] = CELL.CARROT

    const canSwitch =
      this.rowCountSinceLastLaneSwitch >= this.minRowCountUntilNewLaneSwitch

    if (canSwitch && Math.random() < this.laneSwitchChance) {
      this.switchSafePathLane()
      row[this.safePathX] = CELL.CARROT
    } else {
      this.rowCountSinceLastLaneSwitch++
    }

    return row
  }

  switchSafePathLane() {
    if (this.safePathX === 0 || this.safePathX === this.COL_COUNT - 1) {
      // If we're on either one of the outside lanes, switch to center lane
      this.safePathX = 1
    } else {
      // If we're on center lane, randomly go left or right
      this.safePathX += Math.random() >= 0.5 ? 1 : -1
    }

    this.rowCountSinceLastLaneSwitch = 0
  }

  /**
   * Based on blueprint, returns new one with traps.
   */
  processBlueprint_addTraps(source: SectionBlueprint): SectionBlueprint {
    return source.map(row => {
      this.rowCountSinceLastTrap += 1

      if (this.rowCountSinceLastTrap >= this.minRowCountUntilNewTrap) {
        this.rowCountSinceLastTrap = 0

        const combo = this.$combo.get()
        this.minRowCountUntilNewTrap = MathUtils.randInt(
          lerpDifficultyProperty(PARAMS, 'minDistanceBetweenTraps', combo),
          lerpDifficultyProperty(PARAMS, 'maxDistanceBetweenTraps', combo)
        )

        return row.map(cell => {
          switch (cell) {
            case CELL.CARROT:
              return CELL.CARROT
            case CELL.EMPTY:
            default:
              if (Math.random() < 0.75) {
                return CELL.TRAP
              } else {
                return CELL.EMPTY
              }
          }
        })
      } else {
        return row
      }
    })
  }

  /**
   * Based on blueprint, returns new one with some carrots removed. This helps breaks monotonys.
   */
  processBlueprint_removeCarrots(source: SectionBlueprint): SectionBlueprint {
    return source.map(row => {
      return row.map(cell => {
        if (cell === CELL.CARROT) {
          return Math.random() <
            lerpDifficultyProperty(
              PARAMS,
              'removeCarrotChance',
              this.$combo.get()
            )
            ? CELL.EMPTY
            : CELL.CARROT
        } else {
          return cell
        }
      })
    })
  }

  /**
   * Based on blueprint, adds extra carrots.
   * This spice things up, creating branching outside of safe path that the user might go to at risk of going into a trap.
   */
  processBlueprint_addDecoyCarrots(source: SectionBlueprint): SectionBlueprint {
    return source.map(row => {
      return row.map(cell => {
        if (cell === CELL.EMPTY) {
          return Math.random() < PARAMS.section.decoyCarrotChance
            ? CELL.CARROT
            : CELL.EMPTY
        } else {
          return cell
        }
      })
    })
  }
}

class CarrotSurfers extends Core {
  protected cameraPositionMobile = new Vector3(0, 10, 10)
  protected cameraPositionDesktop = new Vector3(0, 10, 10)

  public keys: Record<string, { isDown: boolean; isJustDown: boolean }> = {}

  protected playerLane: -1 | 0 | 1 = 0

  protected animation?: gsap.core.Tween

  protected player!: Object3D
  protected playerLeftEar!: Object3D
  protected playerRightEar!: Object3D
  protected playerTail!: Object3D
  protected playerBounceTweens: gsap.core.Tween[] = []
  protected playerRespawnTL?: gsap.core.Timeline

  protected lanes: Mesh[] = []
  protected laneMaterial!: MeshBasicMaterial

  protected speed = PARAMS.difficulty.lowDifficulty.speed

  protected sections: LevelSection[] = []
  protected sectionGenerator!: SectionGenerator
  protected SECTION_LANE_PREFAB!: Mesh

  private _tmp_Vector3 = new Vector3()

  async init(params: CoreInitParameters) {
    this.initSectionLabPrefab()
    this.sectionGenerator = new SectionGenerator(this.store.$combo)

    this.keys[KEY.LEFT] = {
      isDown: false,
      isJustDown: false,
    }

    this.keys[KEY.RIGHT] = {
      isDown: false,
      isJustDown: false,
    }

    this.onKeyDown = this.onKeyDown.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)

    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)

    await super.init(params)

    this.initPlayer()
    this.initPlayerBounceAnimation()
    this.initSections()

    const ground = this.assets.get('tile')!.clone()
    ground.scale.set(20, 1, 20)
    this.scene.add(ground)
  }

  protected initCamera() {
    super.initCamera()

    this.camera.position.set(0, 10, 10)
    this.camera.lookAt(0, 0, 0)

    if (this.debugCamera) {
      this.debugCamera.position.set(0, 60, 0)
    }

    if (this.debugCameraControls) {
      this.debugCameraControls.target.set(0, 0, 0)
      this.debugCameraControls.update()
    }
  }

  /**
   * Create multiple sections at init.
   * Enough so that it covers the whole viewport.
   */
  protected initSections() {
    for (let i = 0; i < PARAMS.section.count; i++) {
      this.addLevelSection()
    }
  }

  protected initSectionLabPrefab() {
    this.SECTION_LANE_PREFAB = new Mesh(
      new PlaneGeometry(0.125, 1, 1, 1),
      new ShaderMaterial({
        fragmentShader: laneFragmentShader,
        vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
        transparent: true,
      })
    )
    this.SECTION_LANE_PREFAB.rotation.x = MathUtils.degToRad(-90)
  }

  initPlayer() {
    this.player = this.assets.get('rabbit')!
    this.player.rotateY(MathUtils.degToRad(180))
    this.player.position.z = 4
    this.scene.add(this.player)

    this.playerLeftEar = this.player.getObjectByName('earLeft')!
    this.playerRightEar = this.player.getObjectByName('earRight')!

    this.playerTail = this.player.getObjectByName('tail')!
  }

  initPlayerBounceAnimation() {
    const duration = 0.05

    const earRotationXTweensParams = {
      from: {
        x: 0.1,
      },
      to: {
        x: -0.4,
        ease: 'sine.out',
        yoyoEase: 'sine.in',
        yoyo: true,
        repeat: -1,
        duration: duration,
        delay: duration,
      },
    }

    const earRotationZTweensParams = {
      from: {
        z: 0.1,
      },
      to: {
        z: -0.1,
        ease: 'linear',
        yoyo: true,
        repeat: -1,
        duration: duration * 2,
      },
    }

    let yoyoBack = false

    // Body bounce
    this.playerBounceTweens.push(
      gsap.fromTo(
        this.player.position,
        {
          y: 0,
        },
        {
          y: 0.25,
          ease: 'sine.out',
          yoyoEase: 'sine.in',
          yoyo: true,
          repeat: -1,
          duration,
          onRepeat: () => {
            if (yoyoBack) {
              this.spawnDustParticlesUnderPlayer()
            }

            yoyoBack = !yoyoBack
          },
        }
      )
    )

    // Left ear X rotation
    this.playerBounceTweens.push(
      gsap.fromTo(
        this.playerLeftEar.rotation,
        earRotationXTweensParams.from,
        earRotationXTweensParams.to
      )
    )

    // Left ear Z rotation
    this.playerBounceTweens.push(
      gsap.fromTo(
        this.playerLeftEar.rotation,
        earRotationZTweensParams.from,
        earRotationZTweensParams.to
      )
    )

    // Right ear X rotation
    this.playerBounceTweens.push(
      gsap.fromTo(this.playerRightEar.rotation, earRotationXTweensParams.from, {
        ...earRotationXTweensParams.to,
        delay: duration + 0.01,
      })
    )

    // Right ear Z rotation
    this.playerBounceTweens.push(
      gsap.fromTo(
        this.playerRightEar.rotation,
        earRotationZTweensParams.from,
        earRotationZTweensParams.to
      )
    )

    // Tail bounce
    this.playerBounceTweens.push(
      gsap.fromTo(
        this.playerTail.rotation,
        {
          x: -1,
        },
        {
          x: 1,
          ease: 'sine.out',
          yoyoEase: 'sine.in',
          yoyo: true,
          repeat: -1,
          duration: duration,
        }
      )
    )

    this.playerBounceTweens.push(
      gsap.fromTo(
        this.playerTail.rotation,
        {
          y: 1,
        },
        {
          y: -1,
          ease: 'sine.out',
          yoyoEase: 'sine.in',
          yoyo: true,
          repeat: -1,
          duration: duration * 2,
        }
      )
    )
  }

  protected onKeyDown(e: KeyboardEvent) {
    if (
      this.isContainerWithinViewport &&
      (e.code === KEY.LEFT || e.code === KEY.RIGHT)
    ) {
      if (!this.keys[e.code].isDown) {
        this.keys[e.code].isDown = true
        this.keys[e.code].isJustDown = true
        this.store.globalPinia.clicks += 1
        this.store.$interactedWith.set(true)
      }
    }
  }

  protected onKeyUp(e: KeyboardEvent) {
    if (e.code === KEY.LEFT || e.code === KEY.RIGHT) {
      this.keys[e.code].isDown = false
    }
  }

  protected onRender(interp: number, deltaTime: number): void {
    const items = this.sections.flatMap(s => s.items)

    items.forEach(item => {
      switch (item._type) {
        case CELL.CARROT:
          // Slower rotation than other games because motion sickness is strong here
          item.holder.rotation.y += deltaTime * CARROT_ROTATION_SPEED * 0.5
          break
        case CELL.TRAP:
          if (item.mixer) {
            item.mixer.update(deltaTime / 1000)
          }
        default:
          break
      }
    })

    this.updatePlayerBounceTweenSpeed()

    super.onRender(interp, deltaTime)
  }

  protected onFixedUpdate(timestep: number) {
    this.store.globalPinia.duration.carrotSurfer += timestep

    if (this.rabbitState !== RABBIT_STATE.ALIVE) {
      return
    }

    this.speed = this.getSpeed()

    // Input management
    if (this.keys[KEY.LEFT].isJustDown && this.playerLane > -1) {
      this.switchLane(-1)
    } else if (this.keys[KEY.RIGHT].isJustDown && this.playerLane < 1) {
      this.switchLane(1)
    }
    this.keys[KEY.LEFT].isJustDown = false
    this.keys[KEY.RIGHT].isJustDown = false

    for (let i = this.sections.length - 1; i >= 0; i--) {
      const section = this.sections[i]

      section.position.z += this.speed

      // Section is close enough to the player and becomes eligible to items collision detection
      section.collisionDetectionEnabled =
        this.player.position.distanceTo(section.position) < section.length / 2

      if (section.collisionDetectionEnabled) {
        section.items.forEach(item => {
          this.checkPlayerItemCollision(item)
        })
      }

      // Section scrolls outside of playground
      if (section.position.z > PARAMS.lane.length / 2 + section.length / 2) {
        section.dispose()
        this.scene.remove(section)
        this.sections.splice(i, 1)
        this.addLevelSection()
      }
    }

    this.keys[KEY.LEFT].isJustDown = false
    this.keys[KEY.RIGHT].isJustDown = false
  }

  protected explode() {
    this.store.$combo.set(0)

    this.store.$deaths.set(this.store.$deaths.get() + 1)

    this.player.visible = false

    this.speed = 0

    this.particlesDeadRabbit(this.player.position)

    this.cameraShake(0.1)

    this.rabbitState = RABBIT_STATE.DEAD

    // Reset keys to prevent lane transition while rabbit is resurecting
    this.keys[KEY.LEFT].isJustDown = false
    this.keys[KEY.RIGHT].isJustDown = false

    this.playerRespawnTL?.kill()
    this.playerRespawnTL = gsap
      .timeline({
        delay: PARAMS.respaw.delay,
        onComplete: () => {
          this.rabbitState = RABBIT_STATE.ALIVE
        },
      })
      .add(() => {
        this.player.visible = true
      })
      .fromTo(
        this.player.scale,
        {
          x: 0,
          y: 0,
          z: 0,
        },
        {
          x: 1,
          y: 1,
          z: 1,
          ease: RABBIT_RESPAWN_SCALE_EASE,
          duration: RABBIT_RESPAWN_SCALE_DURATION,
        }
      )
      .to(this, {
        speed: PARAMS.difficulty.lowDifficulty.speed,
        ease: 'power2.in',
      })
  }

  /**
   * Spawn dust particle cloud at player position but attach particles to the scrolling section underneath player.
   * This helps sell the effect that player is moving because particles will move with section.
   */
  protected spawnDustParticlesUnderPlayer() {
    // Find section underneath player
    const section = this.sections.find(
      s =>
        s.position.z <= this.player.position.z + s.length / 2 &&
        s.position.z >= this.player.position.z - s.length / 2
    )

    if (!section) {
      return
    }

    const position = this.player.position.clone()
    position.copy(section.worldToLocal(position))

    // this.particlesDustPoof(position, section)
    this.spawnParticles({
      pos: position,
      count: 10,
      mesh: this.particles.groundish,
      scale: {
        min: 0.5,
        max: 2,
      },
      xRandomSpread: 1,
      yRandom: {
        min: 0,
        max: 1,
      },
      zRandom: {
        min: -0.5,
        max: 0.5,
      },
      duration: {
        min: 0.5,
        max: 1,
      },
      parent: section,
      invertScale: true,
    })
  }

  protected checkPlayerItemCollision(item: Item) {
    item.getWorldPosition(this._tmp_Vector3)
    if (
      item.collisionDetectionEnabled &&
      this.player.position.distanceTo(this._tmp_Vector3) <
        PARAMS.player.collisionRadius
    ) {
      item.collisionDetectionEnabled = false

      if (item._type === CELL.CARROT) {
        item.holder.visible = false

        this.particlesCarrot(VECTOR_ZERO, item)

        this.store.$score.set(this.store.$score.get() + 1)
        this.store.$combo.set(this.store.$combo.get() + 1)
      } else {
        item.playAnimation(this.animationClips.trap)

        // Scale down trap after animation so player can respawn without visual trap.
        gsap.to(item.scale, {
          x: 0,
          y: 0,
          z: 0,
          delay: 1.1,
          ease: 'power4.out',
        })
        this.explode()
      }
    }
  }

  protected getSpeed() {
    return lerpDifficultyProperty(PARAMS, 'speed', this.store.$combo.get())
  }

  protected updatePlayerBounceTweenSpeed() {
    const timescale = this.speed / PARAMS.difficulty.highDifficulty.speed

    this.playerBounceTweens.forEach(tween => {
      tween.timeScale(timescale)
    })
  }

  protected switchLane(direction: -1 | 1) {
    this.playerLane += direction

    this.animation?.kill()
    this.animation = gsap.to(this.player.position, {
      x: this.playerLane * PARAMS.lane.width,
      duration: this.getSwitchLaneAnimationDuration(),
      ease: 'power4.inOut',
    })
  }

  protected getSwitchLaneAnimationDuration(): number {
    return (
      PARAMS.lane.switchAnimationDuration *
      (PARAMS.difficulty.highDifficulty.speed / this.getSpeed())
    )
  }

  public dispose() {
    super.dispose()

    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
  }

  protected addLevelSection() {
    this.sectionGenerator.laneSwitchChance = lerpDifficultyProperty(
      PARAMS,
      'laneSwitchChance',
      this.store.$combo.get()
    )

    let blueprint = this.sectionGenerator.generateSection()
    blueprint = this.sectionGenerator.processBlueprint_addTraps(blueprint)
    blueprint = this.sectionGenerator.processBlueprint_removeCarrots(blueprint)
    blueprint =
      this.sectionGenerator.processBlueprint_addDecoyCarrots(blueprint)

    const section = new LevelSection({
      blueprint,
      position: this.computeNextSectionPosition(),
      assets: {
        carrot: this.assets.get('carrot')!,
        trap: this.assets.get('trap')!,
      },
      prefab: this.SECTION_LANE_PREFAB,
    })

    this.scene.add(section)
    this.sections.push(section)
  }

  protected computeNextSectionPosition() {
    if (this.sections.length < 1) {
      return 0
    }

    // Get the section with the lowest z position
    const sumWithInitial = this.sections.reduce((a, b) => {
      if (a.position.z < b.position.z) {
        return a
      }

      return b
    })

    return sumWithInitial.position.z - PARAMS.section.length * PARAMS.cellSize
  }

  protected initDebug(): void {
    super.initDebug()

    if (!this.debug) {
      return
    }

    this.pane
      .addBinding(this.camera, 'fov', {
        min: 0,
        max: 200,
      })
      .on('change', () => {
        this.updateCamera()
      })

    if (!this.debug) {
      return
    }
  }
}

export { CarrotSurfers }
